import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonComponent, ButtonLeft, ButtonRight, FileBox, Form, InputDiv, SvgDiv, UploadDiv } from './styles';
import { CommentFilesInterface, LinkObject } from '../../../interfaces/TaskInterface';
import TiptapEditor from '../../tiptapEditor';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import Button from '../../button';
import { captureException } from '../../../services/logService';
import { RenderFilePreview } from '../../renderFilePreview';
import {
  convertBase64ToFile,
  getFirebaseUrlFromFile,
  handleFileUpload,
  isEmpty,
  isEmptyMessage,
  isMediaRecordingSupported,
  removeFileFromFirebase
} from '../../../helpers/common';
import Modal from '../modal';
import SnapRecordModal from '../snapRecordModal';
import RecordingSnapAttach from '../../recording';
import InsertGifModal from '../insertGifModal';
import { sendFeedbackMessage } from '../../../services/helpCenterService';
import { rootStore } from '../../../mobx/rootStore';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import ModalHeader from '../modalHeader';

interface Props {
  openSuccessFullModal: () => void;
  closeContactUsModal: () => void;
  disable?: boolean;
}

export const ContactUsModal: React.FC<Props> = (props) => {
  // props
  const { openSuccessFullModal, closeContactUsModal, disable = false } = props;
  // Refs
  const editorRef = useRef<TiptapEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // State
  const [message, setMessage] = useState<string>();
  const [uploadedFile, setUploadedFile] = useState<LinkObject[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [requiredHeight, setRequiredHeight] = useState(320);
  const [isSnapModalOpen, setIsSnapModalOpen] = useState(false);
  const [openGifModal, setIsOpenGifModal] = useState(false);

  // Mobx store variables
  const { projectStore, currentUser } = rootStore;
  const { uploadFilePercent } = projectStore;

  // height calculate for input when file is uploaded
  const calculateHeight = useCallback(() => {
    let newHeight;
    if (uploadedFile.length === 0) {
      newHeight = 320;
    } else if (uploadedFile.length <= 2) {
      newHeight = 249;
    } else if (uploadedFile.length < 5) {
      newHeight = 194;
    } else {
      newHeight = 137;
    }
    setRequiredHeight(newHeight);
  }, [uploadedFile?.length]);

  useEffect(() => {
    calculateHeight();
  }, [calculateHeight]);

  const onClickSendMessage = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
        const attachment = uploadedFile?.map((file) => ({
          filename: file?.name,
          url: file?.href
        }));
        let commentData = message || '';
        const pattern = /<p><span>​<\/span><\/p>|<p><br><\/p>|<p><br><br><\/p>/g;
        if (!isEmpty(message)) {
          while (pattern.test(commentData)) {
            commentData = commentData.replace(pattern, '');
          }
          if (commentData.match(/<br\s*>\s*<\/p>$/)) {
            commentData = commentData.replace(/<br\s*>\s*<\/p>$/, '</p>');
          }
        }

        const tempElement = document.createElement('div');
        tempElement.innerHTML = commentData || '';
        const linkElements = Array.from(tempElement.querySelectorAll('img'));

        for (const linkElement of linkElements) {
          const srcLink = linkElement?.getAttribute('src');
          if (
            !isEmpty(srcLink) &&
            !srcLink?.startsWith('https://firebasestorage') &&
            !srcLink?.startsWith('https://figma') &&
            !srcLink?.startsWith('/static') &&
            !srcLink?.startsWith('https://')
          ) {
            const fileData = await convertBase64ToFile(srcLink);
            const fileUrl = await getFirebaseUrlFromFile({ file: fileData }, 'files/');
            if (fileUrl) {
              commentData = commentData?.replace(srcLink || '', fileUrl);
            }
          }
        }

        if (isEmpty(commentData) || isEmptyMessage(commentData)) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter a message.');
          return;
        }
        if (!currentUser?.name && currentUser?.email) {
          const payload = {
            senderName: currentUser?.name,
            senderEmail: currentUser?.email,
            message: commentData,
            attachments: attachment
          };

          const result = await sendFeedbackMessage(payload);
          if (result) {
            closeContactUsModal();
            openSuccessFullModal();
          }
        } else {
          rootStore.alertNotificationStore.setErrorMessage('Current user name or email missing!');
        }
      } catch (error: unknown) {
        console.error('Error sending feedback message:', error);
      } finally {
        setLoading(false);
      }
    },
    [closeContactUsModal, message, openSuccessFullModal, uploadedFile, currentUser?.email, currentUser?.name]
  );

  const handleFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files?.length === 0) {
        rootStore.alertNotificationStore.setErrorMessage('No files selected.');
        return;
      }
      try {
        setFileUploading(true);
        const fileArray = Array.from(files); // Convert FileList to Array
        //Call common function to upload file in firebase
        const updatedFiles = await handleFileUpload(uploadedFile || [], fileArray, editorRef, true);
        const value = [...(uploadedFile || []), ...(updatedFiles || [])];
        setUploadedFile(value);
      } catch (error) {
        captureException(error);
        rootStore.alertNotificationStore.setErrorMessage('Error occurred while uploading files.');
      } finally {
        setFileUploading(false);
      }
    },
    [uploadedFile]
  );

  const onClickClose = useCallback(
    (index: number, file: LinkObject) => {
      if (file && file.href.startsWith('https://firebasestorage.googleapis')) removeFileFromFirebase(file.href);
      const filesClone = JSON.parse(JSON.stringify(uploadedFile));
      filesClone.splice(index, 1);
      setUploadedFile(filesClone);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadedFile]
  );

  const addDroppedFiles = useCallback((files: CommentFilesInterface[]) => {
    setUploadedFile((prevFiles: CommentFilesInterface[]) => [...(prevFiles || []), ...(files || [])]);
  }, []);

  const handleOpenSnapModal = useCallback(() => {
    setIsSnapModalOpen(true);
  }, []);

  const handleCloseSnapModal = useCallback(() => {
    setIsSnapModalOpen(false);
  }, []);

  const uploadSnapEmbed = useCallback((firebaseUrl: string, fileName: string, _videoId: string) => {
    const fileObj = {
      name: fileName,
      href: firebaseUrl,
      size: '0',
      file_type: 'mp4'
    };
    setUploadedFile((prevFiles: CommentFilesInterface[]) => [...(prevFiles || []), fileObj]);
  }, []);

  const handleOpenGifModal = useCallback(() => {
    setIsOpenGifModal(true);
  }, []);

  const handleCloseGifModal = useCallback(() => {
    setIsOpenGifModal(false);
  }, []);

  return (
    <>
      <ModalHeader title='Contact us' description='You can also email us at support@teamcamp.app' />
      <Form maxHeight={requiredHeight}>
        <InputDiv>
          <TiptapEditor
            ref={editorRef}
            valueContent=''
            setValueContent={(value: string) => setMessage(value)}
            placeholder='How can we help you?'
            upload={{
              addUploadedFiles: (value: CommentFilesInterface[]) => addDroppedFiles(value),
              setLoading,
              setStateLoading: setFileUploading
            }}
            toolbarActions={{
              recordSnapModalOpen: handleOpenSnapModal,
              handleFileInput,
              handleGifModal: handleOpenGifModal
            }}
            dropBox={{ isVisible: true }}
          />
          {isMediaRecordingSupported() && (
            <RecordingSnapAttach setLoading={setLoading} uploadSnapEmbed={uploadSnapEmbed} />
          )}
          <input type='file' ref={fileInputRef} hidden onChange={onFileUpload} multiple aria-label='Upload file' />
          {/* make upload file working  */}
          {!disable && uploadedFile?.length > 0 && (
            <FileBox>
              {uploadedFile?.length > 0 &&
                uploadedFile?.map((file: LinkObject, index: number) => {
                  return (
                    <RenderFilePreview
                      key={index}
                      file={file}
                      isClose={true}
                      maxWidth={227}
                      onClose={() => onClickClose(index, file)}
                    />
                  );
                })}
            </FileBox>
          )}
        </InputDiv>
      </Form>
      <ButtonComponent>
        <ButtonLeft>
          <SvgDiv
            role='button'
            onClick={() => {
              handleFileInput();
            }}>
            <SVGIcon
              name='file-attachment-icon'
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              className='attachment-fill-Color'
            />
          </SvgDiv>
          {fileUploading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}
        </ButtonLeft>
        <ButtonRight>
          <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} onClick={closeContactUsModal} />
          <Button title='Send message' variant={BUTTON_TYPE.PRIMARY} onClick={onClickSendMessage} isLoading={loading} />
        </ButtonRight>
      </ButtonComponent>
      {isMediaRecordingSupported() && (
        <Modal open={isSnapModalOpen} onClose={handleCloseSnapModal} maxWidth={'258px'}>
          <SnapRecordModal handleCloseModal={handleCloseSnapModal} />
        </Modal>
      )}
      <Modal open={openGifModal} onClose={handleCloseGifModal} maxWidth={'686px'} isResponsiveMaxWidth={true}>
        <InsertGifModal
          setSelectedGif={(url: string) => {
            editorRef.current?.addImage(url);
          }}
          closeModal={handleCloseGifModal}
        />
      </Modal>
    </>
  );
};
